import { Box, Flex, Text, VStack } from '@minetrace/ui'
import { LuRefreshCw, LuScale } from 'react-icons/lu'
import type { VerificacionPublicaResponse } from '../../api/models/response/indiceDigital'
import { formatDateTime } from '../../lib/dates'
import { describeFolios, describeVerificacionPublica } from './verificacionPublica'

/**
 * EL VEREDICTO DE UN ÍNDICE DIGITAL, LEÍDO POR UN CIUDADANO (D.S. 029-2021-PCM
 * arts. 36-37 y 45).
 *
 * <p>QUIEN ABRE ESTO NO ES UN USUARIO DEL SISTEMA. Es quien tiene el índice impreso
 * delante: un ciudadano, un auditor o un funcionario, sin cuenta y sin contexto
 * alguno sobre MineTrace. Todo lo que la pantalla afirme lo va a leer alguien que no
 * puede contrastarlo con nada más.</p>
 *
 * <p>DE LOS CUATRO DESENLACES SÓLO UNO ES UNA ACUSACIÓN. El estilo lo decide
 * `describeVerificacionPublica`, entrada por entrada; aquí no hay ninguna condición
 * del tipo "válido / no válido". `NoComprobable` y `NoDisponible` salen en tono
 * neutro y con su frase propia, y `NoDisponible` además ofrece reintentar: una caída
 * de Cloud KMS no puede publicarse como una falsificación de una empresa minera.</p>
 *
 * <p>LAS LIMITACIONES SE ENSEÑAN SIEMPRE, TAMBIÉN EN VERDE. Es el mismo texto que
 * imprime el PDF (`IndicePdfGenerator.Leyendas`) y que muestra la pestaña de
 * metadatos de la vista interna: si el papel lo dice, la pantalla también, y con las
 * mismas palabras.</p>
 */
export function IndiceResultado({
  data,
  onReintentar,
}: {
  data: VerificacionPublicaResponse
  onReintentar: () => void
}) {
  const visual = describeVerificacionPublica(data.estado)
  const Icono = visual.icon

  return (
    <Box bg="white" borderRadius="xl" shadow="0 8px 32px rgba(0,0,0,0.06)" overflow="hidden">
      {/* Veredicto. El estado se dice con icono + título + frase del backend; el
          color es refuerzo y nunca el único portador del significado. */}
      <Box bg={visual.bg} px={6} py={5} borderBottom="1px solid" borderColor={visual.borderColor}>
        <Flex alignItems="flex-start" gap={3}>
          <Box color={visual.color} flexShrink={0} mt={0.5} aria-hidden="true">
            <Icono size={26} />
          </Box>
          <Box>
            <Text fontSize="lg" fontWeight="700" color={visual.color} lineHeight="1.3">
              {visual.titulo}
            </Text>
            {/* `mensaje` viene de una LISTA CERRADA de cuatro frases redactadas en el
                backend. Se enseña TAL CUAL: ni se reescribe ni se le añade nada que
                le cambie el sentido. */}
            <Text fontSize="sm" color="#414844" mt={1.5} lineHeight="1.6">
              {data.mensaje}
            </Text>
          </Box>
        </Flex>

        {visual.reintentable && (
          <Box
            as="button"
            type="button"
            display="flex"
            alignItems="center"
            gap={2}
            mt={4}
            ml="38px"
            px={4}
            py={2}
            borderRadius="sm"
            bg="#b4532a"
            color="white"
            fontSize="sm"
            fontWeight="600"
            cursor="pointer"
            _hover={{ opacity: 0.9 }}
            onClick={onReintentar}
          >
            <LuRefreshCw size={15} />
            Volver a intentarlo
          </Box>
        )}
      </Box>

      {/* Los datos sellados. Es exactamente lo que el DTO público publica: ni un
          campo más. Lo que permite a quien verifica contrastar el papel. */}
      <Box px={6} py={6}>
        <Text
          fontSize="xs"
          fontWeight="700"
          color="#857b71"
          textTransform="uppercase"
          letterSpacing="0.05em"
          mb={4}
        >
          Datos del índice registrado
        </Text>

        <VStack gap={5} alignItems="stretch">
          <Flex gap={8} flexWrap="wrap">
            <Dato label="Expediente" value={data.expedienteCode} mono flex={1} minW="200px" />
            <Dato label="Versión sellada" value={`N.º ${data.versionNumber}`} />
          </Flex>

          <Box h="1px" bg="#e0e3e1" />

          <Flex gap={8} flexWrap="wrap">
            <Dato label="Titular" value={data.titularRazonSocial ?? '—'} flex={2} minW="200px" />
            <Dato label="RUC" value={data.titularRuc ?? '—'} mono flex={1} minW="140px" />
          </Flex>

          <Flex gap={8} flexWrap="wrap">
            <Dato
              label="Fecha de sellado"
              value={formatDateTime(data.generatedAt)}
              flex={1}
              minW="200px"
            />
            <Dato label="Alcance" value={describeFolios(data.folioFrom, data.folioTo)} flex={1} minW="150px" />
            <Dato
              label="Documentos"
              value={String(data.itemCount)}
              flex={1}
              minW="120px"
            />
          </Flex>
        </VStack>
      </Box>

      {/* Qué acredita — SÓLO cuando la firma sostiene el documento. Enunciarlo en
          cualquier otro estado sería afirmar algo que no se ha podido comprobar. */}
      {visual.acredita && (
        <Box px={6} pb={6}>
          <Box p={4} borderRadius="lg" bg="#eef7f6" borderLeft="3px solid" borderColor="#1d6664">
            <Text fontSize="sm" fontWeight="700" color="#1d6664" mb={1}>
              Qué acredita esta comprobación
            </Text>
            <Text fontSize="xs" color="#414844" lineHeight="1.7">
              Que el expediente {data.expedienteCode}, a nombre de{' '}
              {data.titularRazonSocial ?? 'su titular'}, quedó sellado en su versión{' '}
              {data.versionNumber} cubriendo {data.itemCount} documento(s) y{' '}
              {describeFolios(data.folioFrom, data.folioTo).toLowerCase()}, y que ese índice
              sellado no ha sido alterado desde entonces. El código acredita únicamente lo que
              constaba AL SELLAR esa versión: el expediente puede haber seguido recibiendo
              documentos después, lo cual es normal en un expediente de sólo añadido y no indica
              manipulación.
            </Text>
          </Box>
        </Box>
      )}

      {/* Qué NO acredita — SIEMPRE, incluso en verde. Es el punto entero de la
          página: un documento con aspecto oficial, un código y un QR se lee como si
          acreditara mucho más de lo que acredita. */}
      <Box px={6} pb={6}>
        <Flex alignItems="center" gap={2} mb={3}>
          <Box color="#b4532a">
            <LuScale size={16} />
          </Box>
          <Text fontSize="sm" fontWeight="700" color="#2a2622">
            Lo que esta comprobación no acredita
          </Text>
        </Flex>
        <VStack gap={3} alignItems="stretch">
          {LIMITES.map((limite) => (
            <Box
              key={limite.titulo}
              p={4}
              borderRadius="lg"
              bg="#f1f4f2"
              borderLeft="3px solid"
              borderColor="#c1c8c2"
            >
              <Text fontSize="sm" fontWeight="700" color="#2a2622" mb={1}>
                {limite.titulo}
              </Text>
              <Text fontSize="xs" color="#414844" lineHeight="1.7">
                {limite.cuerpo}
              </Text>
            </Box>
          ))}
        </VStack>
      </Box>
    </Box>
  )
}

/**
 * LAS DOS LIMITACIONES QUE AFECTAN A LO QUE ESTA PÁGINA AFIRMA, palabra por palabra
 * como en el PDF (`IndicePdfGenerator.LeyendaFirmaNoIofe` y `LeyendaSinSelloDeTiempo`)
 * y como en la pestaña de metadatos de la vista interna
 * (`expedientes.electronico.limits.*` de `es.json`).
 *
 * <p>EL TEXTO ESTÁ AQUÍ LITERAL Y NO DETRÁS DE `useT`, siguiendo a las demás páginas
 * públicas (`/login`, `/politica-privacidad`) —que tampoco traducen— y porque `en.json`
 * y `qu.json` no tienen la sección `expedientes`: pasar por i18n aquí significaría que
 * un visitante con el navegador en inglés se quedaría sin la advertencia legal.</p>
 *
 * <p>Las otras dos leyendas del PDF (fedatario y plazos de conservación) NO salen aquí:
 * hablan del listado de documentos y del resumen por serie, y esta página no publica ni
 * lo uno ni lo otro.</p>
 */
const LIMITES: { titulo: string; cuerpo: string }[] = [
  {
    titulo: 'La firma no está acreditada ante la IOFE',
    cuerpo:
      'El índice se firma con una clave criptográfica propia de KUNTUR custodiada en Google ' +
      'Cloud KMS. Eso aporta integridad técnica y no repudio, pero NO la equivalencia legal ' +
      'con la firma manuscrita, que sólo confiere un certificado de una entidad de ' +
      'certificación acreditada en la Infraestructura Oficial de Firma Electrónica del Perú ' +
      '(Ley 27269). Este índice no lleva una firma de ese tipo y no debe presentarse como si ' +
      'la llevara.',
  },
  {
    titulo: 'La fecha de sellado la pone nuestro servidor, no una TSA',
    cuerpo:
      'Las fechas de sellado proceden del reloj del servidor de KUNTUR. No hay sello de tiempo ' +
      'RFC 3161 de una autoridad de sellado de tiempo acreditada e independiente. Esa fecha ' +
      'acredita cuándo lo registró nuestro sistema, pero no es oponible a un tercero que ' +
      'discuta el momento exacto.',
  },
]

function Dato({
  label,
  value,
  mono,
  flex,
  minW,
}: {
  label: string
  value: string
  mono?: boolean
  flex?: number
  minW?: string
}) {
  return (
    <Box flex={flex} minW={minW}>
      <Text
        fontSize="xs"
        color="#857b71"
        textTransform="uppercase"
        fontWeight="600"
        letterSpacing="0.05em"
      >
        {label}
      </Text>
      <Text
        fontSize="md"
        fontWeight="600"
        color="#2a2622"
        mt={1}
        fontFamily={mono ? 'monospace' : undefined}
        wordBreak={mono ? 'break-all' : undefined}
      >
        {value}
      </Text>
    </Box>
  )
}
